import { ReactNode, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../../components/buttons/button";
import { ContainerCard } from "../../../../components/cards/containerCard";
import { CooperativeMemberCount } from "../../../../components/cards/cooperativeMemberCount";
import { Icon } from "../../../../components/icons/icon";
import { EnforceDirectDebitSetupModal } from "../../../../components/modals/app_modals/debitInstrument/enforceDirectDebitSetupModal";
import { ImageViewModal } from "../../../../components/modals/app_modals/imageViewModal";
import { CooperativeProfilePic } from "../../../../components/profile/cooperativePicture";
import { ProfilePicture } from "../../../../components/profile/profilePicture";
import { useModalControl } from "../../../../customHooks/useModalControl";
import { decodeUrl } from "../../../../helperFunctions";
import {
  MembershipProfileResponse,
  SingleCooperativeDetails,
} from "../../../../redux/features/cooperative/cooperative.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../utils/constants";

export const CooperativeOverview = ({
  cooperativeDetails,
  memberProfile,
  refetchMemberProfile,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  memberProfile?: MembershipProfileResponse;
  refetchMemberProfile: () => void;
}) => {
  const [link, setLink] = useState<string>("");

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ContainerCard className="flex md:!py-10 flex-col gap-6 !rounded-2xl">
          <div className="flex items-center gap-4 w-full">
            <CooperativeProfilePic
              cooperative={cooperativeDetails}
              className="!w-[80px] !h-[80px]"
            />

            <div className="flex flex-col gap-2">
              <p className="text-lg font-medium">
                {cooperativeDetails?.CoopName}
              </p>

              <CooperativeMemberCount
                cooperative={cooperativeDetails}
                className="!text-pry-dark-green-300 !bg-light-purple !mt-3"
              />
            </div>
          </div>

          <hr className="border-pry-dark-green-300/10" />

          <div className="flex flex-col gap-6 w-full">
            <Detail title="Description">
              {cooperativeDetails.description || "No description"}
            </Detail>

            <Detail title="Email Address">
              {cooperativeDetails.CoopEmail}
            </Detail>

            <Detail title="Registration Number">
              {cooperativeDetails.CoopRegistration}
            </Detail>

            <Detail title="Bye Law">
              <Button
                type="button"
                variation="plain"
                className="!px-0 !py-0 md:!px-0 md:py-0 !max-w-fit"
                text="View bye laws"
                icon="small-document-icon"
                onClick={() => {
                  setLink(decodeUrl(cooperativeDetails?.byelaw) || "");
                  handleModalOpen("viewImageModal");
                }}
              />
            </Detail>

            {memberProfile?.data?.collection?.paymentCollectionType ? (
              <Detail
                title="Linked Bank Account / Card"
                className="flex items-center justify-between gap-3"
              >
                {memberProfile?.data?.collection?.paymentCollectionType ===
                "bank"
                  ? `${memberProfile?.data?.collection?.AccountNumber} (${memberProfile?.data?.collection?.Detail?.bank})`
                  : undefined}

                {memberProfile?.data?.collection?.paymentCollectionType ===
                "card" ? (
                  <div className="flex items-center gap-3">
                    ***{memberProfile?.data?.collection?.Detail?.last4} (Exp:{" "}
                    {memberProfile?.data?.collection?.Detail?.exp_month}/
                    {memberProfile?.data?.collection?.Detail?.exp_year})
                    <Icon
                      svg={
                        memberProfile?.data?.collection?.Detail?.card_type ===
                        "visa"
                          ? "visa"
                          : "mastercard"
                      }
                      width={
                        memberProfile?.data?.collection?.Detail?.card_type ===
                        "visa"
                          ? 32
                          : 24
                      }
                      height={
                        memberProfile?.data?.collection?.Detail?.card_type ===
                        "visa"
                          ? 32
                          : 24
                      }
                      className="ml-auto"
                    />
                  </div>
                ) : undefined}

                <button
                  className="w-fit text-pry-green-200 underline"
                  type="button"
                  onClick={() => {
                    handleModalOpen("changeDebitInstrumentModal");
                  }}
                >
                  Change
                </button>
              </Detail>
            ) : undefined}
          </div>
        </ContainerCard>

        <ContainerCard className="flex md:!py-10 flex-col gap-6 !rounded-2xl">
          <Detail
            title="Cooperative Officials"
            className={"flex flex-col gap-1 mt-1"}
          >
            {cooperativeDetails.Official.map((off, idx) => {
              return (
                <div
                  key={`official-${off.User.Email}-${idx}`}
                  className="flex items-center gap-2 px-4 py-1.5 text-xs bg-light-green-bg-600 rounded-lg"
                >
                  <ProfilePicture
                    tempSrc={decodeUrl(off.User.Picture)}
                    initials={`${off.User.FirstName?.slice(
                      0,
                      1
                    )}${off.User.LastName?.slice(0, 1)}`}
                    className="border border-pry-green-200"
                  />
                  <p className="flex-1 text-pry-dark-green-300">
                    {off.User.FirstName} {off.User.LastName}
                  </p>
                  <p className="text-pry-dark-green-300/50 capitalize">
                    {off.Role.Name}
                  </p>
                </div>
              );
            })}
          </Detail>
        </ContainerCard>
      </div>

      <ImageViewModal
        modalOpen={modalOpen && modal === "viewImageModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        src={link}
      />

      <EnforceDirectDebitSetupModal
        isModalOpen={modalOpen && modal === "changeDebitInstrumentModal"}
        cooperativeDetails={cooperativeDetails}
        type="change"
        onClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {
          refetchMemberProfile();
        }}
      />
    </>
  );
};

const Detail = ({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={`flex flex-col gap-1 text-sm text-pry-dark-green-400`}>
      <p className="font-bold">{title}</p>

      <div className={`text-pry-dark-green-400/80 ${className}`}>
        {children}
      </div>
    </div>
  );
};

export const CooperativeOverviewLoader = () => {
  return (
    <ContainerCard className="flex !py-8 flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 w-full">
      <div className="flex flex-col items-center justify-center w-full">
        <Skeleton
          width={80}
          height={80}
          circle
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
        />

        <Skeleton
          width={157}
          height={28}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
        />

        <Skeleton
          width={119}
          height={32}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="!rounded-full"
        />
      </div>

      <div
        className="
          flex flex-col gap-6
          flex-1 border-t-[1.5px] lg:border-t-0 lg:border-l-[1.5px]
          min-w-[300px] w-full
          py-6 md:p-[56px]
          "
      >
        {[1, 2, 3, 4].map((i) => {
          return (
            <div className="flex flex-col gap-1" key={`${i}`}>
              <Skeleton
                width={"50%"}
                height={20}
                baseColor={SKELETON_BASE_COLOR}
                highlightColor={SKELETON_HIGHLIGHT_COLOR}
              />
              <Skeleton
                width={"100%"}
                height={20}
                baseColor={SKELETON_BASE_COLOR}
                highlightColor={SKELETON_HIGHLIGHT_COLOR}
              />
            </div>
          );
        })}
      </div>
    </ContainerCard>
  );
};
